import React, { useState, useEffect } from 'react';
import { Head } from '@inertiajs/react';
import axios from 'axios';
import { Dog, GlassWater, SquareParking, Trees, CalendarPlus,
    Watch, Trash2, DoorOpen, Syringe, Heart, Ambulance,
    MountainSnow, FishOff, Shovel, Loader2
 } from 'lucide-react';
import Header from '@/Components/Built/Website/Header';
import MapboxLocation from '@/Components/Built/Website/MapLocation';
import DosAndDonts from '@/Components/Built/Website/DosAndDonts';
import Notes from '@/Components/Built/Website/Notes';
import FieldHeader from '@/Components/Built/Website/FieldHeader';
import FieldDetails from '@/Components/Built/Website/FieldDetails';
import Footer from '@/Components/Built/Website/Footer';
import BookingForm from '@/Components/Built/Website/BookingForm';
import { FieldPageProps, Pen, Field as FieldType, OpeningTime, Feature as FeatureType } from '@/types/field';
import { User as UserType } from "@/types";
import NewBookingForm from '@/Components/Built/Website/NewBookingForm';
import BookingAlert from '@/Components/Built/Website/BookingAlert';
import Banner from '@/Components/Built/Website/Banner';

interface Note {
    title: string;
    type: string;
    message: string;
    dates: string;
}

const Field: React.FC<{ slug: string; auth: FieldPageProps['auth'] }> = ({ slug, auth }) => {
    const [field, setField] = useState<FieldType | null>(null);
    const [features, setFeatures] = useState<FeatureType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [user, setUser] = useState<UserType | null>(null);

    const [showBookingAlert, setShowBookingAlert] = useState(false);

    const MAP_KEY = "pk.eyJ1IjoiYWxhbmNvbGUiLCJhIjoiY2o2d2dwbDNtMXJ6YjJ4bDFmZWxsZHdtdiJ9.PbKLaM4I5rLeJxQiSOxDLw";

    useEffect(() => {
        const fetchField = async () => {
            try {
                const response = await axios.get(`/api/location/${slug}`);
                setField(response.data.field);
                setFeatures(response.data.features);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching field data:', err);
                setError('Failed to load field data. Please try again later.');
                setLoading(false);
            }
        };

        fetchField();

        setUser(auth.user);

    }, [slug]);

    useEffect(() => {
        if (field?.id) {
            const channel = window.Echo.channel(`field.${field.id}`);

            channel.listen('FieldBooked', (e: any) => {
                setShowBookingAlert(true);
            });

            return () => {
                channel.stopListening('FieldBooked');
            };
        }
    }, [field?.id]);

    const handleCloseAlert = () => {
        setShowBookingAlert(false);
    };

    const handleBookNow = () => {
        console.log('Book Now');
    };

    const dosAndDonts = {
        dos: [
            { text: "Book by appointment only and arrive for your allotted time.", icon: <CalendarPlus className="w-5 h-5 mt-1" /> },
            { text: "Wait your turn if you arrive early and our field is in use.", icon: <Watch className="w-5 h-5 mt-1" /> },
            { text: "Leave promptly when your session has finished - respecting other users.", icon: <Watch className="w-5 h-5 mt-1" /> },
            { text: "Always pick up your doggy's poo and put it in the bin provided.", icon: <Trash2 className="w-5 h-5 mt-1" /> },
            { text: "Always close any gates behind you.", icon: <DoorOpen className="w-5 h-5 mt-1" /> },
            { text: "Ensure your doggy is in good health, fully vaccinated, and up to date with worming, flea and tick treatments.", icon: <Syringe className="w-5 h-5 mt-1" /> },
            { text: "Enjoy your quality time with your doggy in our lovely field.", icon: <Heart className="w-5 h-5 mt-1" /> },
        ],
        donts: [
            { text: "Bring your doggy if they are showing any sign of illness (coughing, diarrhoea, vomiting, general lethargy etc).", icon: <Ambulance className="w-5 h-5 mt-1" /> },
            { text: "Leave your doggy unattended at any time.", icon: <Dog className="w-5 h-5 mt-1" /> },
            { text: "Leave your doggy poo on-site unless in an allocated bin (The doggy do police will be on duty).", icon: <Trash2 className="w-5 h-5 mt-1" /> },
            { text: "Leave any litter on site.", icon: <Trash2 className="w-5 h-5 mt-1" /> },
            { text: "Climb surrounding fencing or gates.", icon: <MountainSnow className="w-5 h-5 mt-1" /> },
            { text: "Interact with any other livestock in the vicinity.", icon: <FishOff className="w-5 h-5 mt-1" /> },
            { text: "Let your doggy dig to Australia - let us know if they have been bad.", icon: <Dog className="w-5 h-5 mt-1" /> },
        ]
    };

    const fieldNotes: Note[] = [
        { type: 'Safety', title: "Mushrooms", message: "Due to the recent downpours, we've noticed mushrooms appearing in some of our fields, which could be harmful to dogs. While our maintenance team are addressing this, we kindly ask you to keep a watchful eye to ensure your dog's stay safe while enjoying the space.", dates: '26th - 30th September' },
        { type: 'General', title: "Dog Waste", message: "We remind all customers to pick up after their dogs. Dog waste can be dangerous to other dogs and is not pleasant when visiting our fields. We have a maintenance plan in place however we do rely on our customers to be responsible dog owners and use tools and bins provided.", dates: 'Always' }
    ];

    if (loading) {
        return (
            <>
                <Head title="Loading..." />
                <Banner />
                <Header user={user} />
                <div className="w-full bg-gray-50 py-32">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <Trees className="w-12 h-12 text-gray-400" />
                            <h2 className="text-xl font-medium text-gray-600">Checking field availability and times</h2>
                            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Head title="Error" />
                <Banner />
                <Header user={user} />
                <div className="w-full bg-gray-50 py-32">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <div className="text-red-500">{error}</div>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!field) {
        return (
            <>
                <Head title="Not Found" />
                <Banner />
                <Header user={user} />
                <div className="w-full bg-gray-50 py-32">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <div className="text-gray-500">No field data available.</div>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const fieldInfo = {
        name: field.name,
        longitude: field.location?.longitude ?? -0.1276,
        latitude: field.location?.latitude ?? 51.5074,
        address: field.address ?? "Address not provided",
        what3words: field.what3words ?? '',
        description: field.description ?? '',
        features: features,
        times: field.openingTimes ?? [],
        prices: field.pens && field.pens.length > 0 ? 
            (field.pens[0].prices && field.pens[0].prices.length > 0 ? field.pens[0].prices : []) : [],
        notes: fieldNotes
    };

    const images = [
        '/images/fields/1.jpg',
        '/images/fields/2.jpg',
        '/images/fields/3.jpg',
        '/images/fields/4.jpg',
        '/images/fields/5.jpg',
        '/images/fields/6.png',
        '/images/fields/7.png',
        '/images/fields/9.png',
    ];

    const mediaItems = field.media;

    const blockedDates = [
        new Date(2024, 6, 4),  // July 4, 2024
        new Date(2024, 6, 13), // December 25, 2024
        new Date(2024, 6, 14), // December 25, 2024
        new Date(2024, 6, 15), // December 25, 2024
    ];

    const isLoggedIn = auth.user !== null;

    return (
        <>
            {showBookingAlert && (
                <BookingAlert
                    message="Field just booked! 🐾"
                    onClose={handleCloseAlert}
                />
            )}
            <Head title={field.name} />
            <Banner />
            <Header user={user} />
            <FieldHeader
                fieldName={field.name}
                distance={field.distance ?? 0}
                onBookNow={handleBookNow}
            />
            <FieldDetails media={mediaItems} fieldInfo={fieldInfo} />

            <div className="w-full mx-auto py-12">
                <div className="flex flex-col lg:flex-row gap-8">
                    <div className="w-full lg:w-full">
                    {field && (
                        <NewBookingForm
                            user={user}
                            field={field}
                            mapboxToken={MAP_KEY}
                        />)
                    }
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default Field;